import { type FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

// MUI
import { Box, Typography } from '@mui/material';

// common
import SchemaForm from 'common/SchemaForm';
import DynamicOperationCodeField from 'common/components/OperationCodeField/DynamicOperationCodeField';
import { formatDateWithoutTimezone } from 'common/utils';

// screen
import DialogWrapper from 'modules/suppliers/detail/components/modals/DialogWrapper';
import PdfViewer from 'modules/suppliers/detail/components/modals/PdfViewer';
import { ContractFromKios } from 'modules/suppliers/services/useSupplyContract';
import { useTabsData } from 'modules/suppliers/detail/context/useTabsData';
import MuiLoading from 'common/components/MuiLoading';
import useSupplyContractDetails from 'modules/suppliers/services/useSupplyContractDetails';
import PdfList from './PdfList';

interface SupplyContractDetailsProps {
  open: boolean;
  onClose: () => void;
  item: ContractFromKios;
}

interface CustomFormData extends ContractFromKios {
  customSwitch: boolean;
}

const SupplyContractDetails: FC<SupplyContractDetailsProps> = (props) => {
  const { open, onClose, item } = props;

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [selectedPdfFileName, setSelectedPdfFileName] = useState<string>('');

  const { formatMessage } = useIntl();

  const { params } = useTabsData();

  const { data } = useSupplyContractDetails({
    supplierProfileOperationCode: params.supplierOperationCode,
    customerOperationCode: params.customerOperationCode,
    contractOperationCode: item.operationCode,
  });

  if (!data) return <MuiLoading />;

  return (
    <DialogWrapper
      title={
        <>
          <FormattedMessage id="supplier.supplyContract.detailsModalTitle" />: {data.contractNumber}
        </>
      }
      open={open}
      onClose={onClose}
    >
      <SchemaForm<CustomFormData>
        formHookProps={{
          values: {
            ...data,
            publishedAt: data.publishedAt ? formatDateWithoutTimezone(data.publishedAt) : '',
            expiredAt: data.isUnlimited
              ? formatMessage({ id: 'noExpiration' })
              : data.expiredAt
                ? formatDateWithoutTimezone(data.expiredAt)
                : '',
            customSwitch: data.expirationDate ? false : true,
            hasPdf: !!data.pdfUrl,
          },
        }}
        globalProps={{ readOnly: true }}
        schema={{
          fields: ({
            formProps: {
              intl: { formatMessage },
            },
          }) => {
            return {
              contractNumber: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.supplyContract.number',
                }),
                placeholder: formatMessage({
                  id: 'supplier.supplyContract.number',
                }),
                rules: { required: true },
              },
              operationCode: {
                mode: 'raw',
                propsWrapper: { desktop: 6 },
                render: (params) => (
                  <DynamicOperationCodeField
                    {...params}
                    label={formatMessage({
                      id: 'supplier.supplyContract.operationCode',
                    })}
                  />
                ),
              },
              name: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({ id: 'supplier.supplyContract.name' }),
                placeholder: formatMessage({
                  id: 'supplier.supplyContract.name',
                }),
                rules: { required: true },
              },
              bankName: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.supplyContract.bankName',
                }),
                placeholder: formatMessage({
                  id: 'supplier.supplyContract.bankName',
                }),
              },
              type: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.supplyContract.classification',
                }),
                placeholder: formatMessage({
                  id: 'supplier.supplyContract.classification',
                }),
                rules: { required: true },
              },
              bankAccount: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.supplyContract.bankAccount',
                }),
                placeholder: formatMessage({
                  id: 'supplier.supplyContract.bankAccount',
                }),
              },
              representative: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.supplyContract.representative',
                }),
                placeholder: formatMessage({
                  id: 'supplier.supplyContract.representative',
                }),
              },
              publishedAt: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.supplyContract.contractSignedDate',
                }),
                placeholder: formatMessage({
                  id: 'supplier.supplyContract.contractSignedDate',
                }),
                rules: { required: true },
              },
              isUnlimited: {
                mode: 'switch',
                propsWrapper: { desktop: 6 },
                label: formatMessage({ id: 'noExpiration' }),
                labelPlacement: 'top',
              },
              expiredAt: {
                mode: 'text-field',
                propsWrapper: { desktop: 6 },
                label: formatMessage({
                  id: 'supplier.supplyContract.expirationDate',
                }),
                placeholder: formatMessage({
                  id: 'supplier.supplyContract.expirationDate',
                }),
                rules: { required: true },
              },
              hasPdf: {
                mode: 'raw',
                render: () => {
                  const pdfPaths = (data.pdfPath as string[]) || [];

                  return !pdfPaths.length ? null : (
                    <Box>
                      <Typography sx={{ marginBottom: 1, fontWeight: 500 }}>
                        PDF Documents:
                      </Typography>
                      <Typography>
                        <PdfList
                          pdfPaths={pdfPaths}
                          onPdfClick={(pdfUrl, fileName) => {
                            setSelectedPdf(pdfUrl);
                            setSelectedPdfFileName(fileName);
                            setPdfModalOpen(true);
                          }}
                        />
                      </Typography>
                    </Box>
                  );
                },
              },
            };
          },
        }}
      />
      <PdfViewer
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfUrl={selectedPdf}
        title={selectedPdfFileName || 'PDF Document'}
      />
    </DialogWrapper>
  );
};

export default SupplyContractDetails;
